import { useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, LogIn } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  Card,
  CardContent,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import {
  type AdoptionReportEntry,
  getTotalEmrFilled,
  getTotalExternalCompletion,
  getPendingVisits,
  getRogerVsExternalPercentage,
} from '../../types/adoptionReport.types';
import { getClinicianFullName } from '../../types/clinician.types';
import {
  getPendingVisits as getVisitPendingVisits,
  getRogerVsExternalPercentage as getVisitRogerVsExternalPercentage,
} from '../../types/visitCompletion.types';
import PageLoader from '../PageLoader';

const SortableHeader = ({
  column,
  children,
  sortColumn,
  sortDirection,
  onSort,
}: {
  column: string;
  children: React.ReactNode;
  sortColumn: string;
  sortDirection: 'asc' | 'desc';
  onSort: (column: string) => void;
}) => (
  <th
    className='cursor-pointer select-none bg-muted border-b-2 border-border p-3 text-left font-semibold text-sm hover:bg-muted/80 transition-colors'
    onClick={() => onSort(column)}
  >
    <div className='flex items-center justify-between pointer-events-none'>
      <span className='font-semibold'>{children}</span>
      {sortColumn === column && sortDirection === 'asc' ? (
        <ChevronUp className='h-4 w-4' />
      ) : sortColumn === column && sortDirection === 'desc' ? (
        <ChevronDown className='h-4 w-4' />
      ) : (
        <ChevronDown className='h-4 w-4 opacity-30' />
      )}
    </div>
  </th>
);

export interface VisitCompletionByClinicianReportTableProps {
  data: AdoptionReportEntry[];
  loading: boolean;
  error: string | null;
  showBreakdown: boolean;
  hideNeverLoggedIn: boolean;
  searchTerm: string;
}

const VisitCompletionByClinicianReportTable = ({
  data,
  loading,
  error,
  showBreakdown,
  hideNeverLoggedIn,
  searchTerm,
}: VisitCompletionByClinicianReportTableProps) => {
  const [sortColumn, setSortColumn] = useState<string>('rogerPercentage');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      if (
        ['pending', 'roger', 'external', 'rogerPercentage'].includes(column)
      ) {
        setSortDirection('desc');
      } else {
        setSortDirection('asc');
      }
    }
  };

  const filteredAndSortedData = useMemo(() => {
    let filtered = data;

    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(entry => {
        const fullName = getClinicianFullName(entry.clinician).toLowerCase();
        const firstName = entry.clinician.firstName.toLowerCase();
        const lastName = entry.clinician.lastName.toLowerCase();
        const title = (entry.clinician.title || '').toLowerCase();

        return (
          fullName.includes(searchLower) ||
          firstName.includes(searchLower) ||
          lastName.includes(searchLower) ||
          title.includes(searchLower)
        );
      });
    }

    if (hideNeverLoggedIn) {
      filtered = filtered.filter(entry => entry.clinician.hasLoggedIn);
    }

    if (sortColumn) {
      filtered = [...filtered].sort((a, b) => {
        let aValue: string | number;
        let bValue: string | number;

        switch (sortColumn) {
          case 'name':
            aValue =
              `${a.clinician.lastName}, ${a.clinician.firstName}`.toLowerCase();
            bValue =
              `${b.clinician.lastName}, ${b.clinician.firstName}`.toLowerCase();
            break;
          case 'title':
            aValue = (a.clinician.title || '').toLowerCase();
            bValue = (b.clinician.title || '').toLowerCase();
            break;
          case 'pending':
            aValue = getPendingVisits(a);
            bValue = getPendingVisits(b);
            break;
          case 'roger':
            aValue = getTotalEmrFilled(a);
            bValue = getTotalEmrFilled(b);
            break;
          case 'external':
            aValue = getTotalExternalCompletion(a);
            bValue = getTotalExternalCompletion(b);
            break;
          case 'rogerPercentage':
            aValue =
              getTotalEmrFilled(a) === 0 && getTotalExternalCompletion(a) === 0
                ? 0
                : getTotalEmrFilled(a) /
                  (getTotalEmrFilled(a) + getTotalExternalCompletion(a));
            bValue =
              getTotalEmrFilled(b) === 0 && getTotalExternalCompletion(b) === 0
                ? 0
                : getTotalEmrFilled(b) /
                  (getTotalEmrFilled(b) + getTotalExternalCompletion(b));
            break;
          default:
            return 0;
        }

        if (typeof aValue === 'string') {
          return sortDirection === 'asc'
            ? aValue.localeCompare(bValue as string)
            : (bValue as string).localeCompare(aValue);
        } else {
          return sortDirection === 'asc'
            ? aValue - (bValue as number)
            : (bValue as number) - aValue;
        }
      });
    }

    return filtered;
  }, [data, searchTerm, sortColumn, sortDirection, hideNeverLoggedIn]);

  return (
    <Card>
      <CardContent className='p-0'>
        {error && (
          <Alert variant='destructive' className='m-4 mb-0'>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {!loading && (
          <div className='text-xs text-muted-foreground px-4 pt-3 pb-2'>
            Showing {filteredAndSortedData.length} of {data.length} clinicians
          </div>
        )}

        <div className='relative min-h-[200px] max-h-[600px] overflow-auto'>
          {loading ? (
            <div className='absolute inset-0 flex items-center justify-center bg-background/80 z-20'>
              <PageLoader message='Loading adoption report data...' />
            </div>
          ) : null}

          <table className='w-full border-collapse'>
            <thead className='sticky top-0 z-10 bg-background shadow-sm'>
              <tr>
                <SortableHeader
                  column='name'
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                >
                  Clinician Name
                </SortableHeader>
                <SortableHeader
                  column='title'
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                >
                  Title
                </SortableHeader>
                <SortableHeader
                  column='pending'
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                >
                  Pending Visits
                </SortableHeader>
                <SortableHeader
                  column='roger'
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                >
                  Completed in Roger
                </SortableHeader>
                <SortableHeader
                  column='external'
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                >
                  Completed Outside of Roger
                </SortableHeader>
                <SortableHeader
                  column='rogerPercentage'
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                >
                  % Completed in Roger
                </SortableHeader>
              </tr>
            </thead>

            <tbody>
              {filteredAndSortedData.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className='p-8 text-center text-muted-foreground'
                  >
                    {loading
                      ? 'Loading…'
                      : error
                        ? ''
                        : 'No clinicians found matching your filters.'}
                  </td>
                </tr>
              ) : (
                filteredAndSortedData.map((entry, index) => {
                  const totalPercentage =
                    getRogerVsExternalPercentage(entry) + '%';

                  return (
                    <tr
                      key={`${entry.clinicianUsername}-${index}`}
                      className='hover:bg-muted/50 border-b border-border'
                    >
                      <td className='p-3'>
                        <div className='flex items-center justify-between'>
                          <span className='font-medium'>
                            {getClinicianFullName(entry.clinician)}
                          </span>
                          {!entry.clinician.hasLoggedIn && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <LogIn className='h-4 w-4 text-destructive cursor-help' />
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>This user has never logged into the app</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                      </td>
                      <td className='p-3'>
                        <span className='text-sm'>
                          {entry.clinician.title || '-'}
                        </span>
                      </td>
                      <td className='p-3'>
                        {showBreakdown ? (
                          <div className='flex items-center gap-2'>
                            <span className='font-medium'>
                              {getPendingVisits(entry)}
                            </span>
                            <div className='text-xs text-muted-foreground'>
                              <div>
                                {getVisitPendingVisits(entry.oasis)} OASIS
                              </div>
                              <div>
                                {getVisitPendingVisits(entry.regular)} Regular
                              </div>
                            </div>
                          </div>
                        ) : (
                          <span className='font-medium'>
                            {getPendingVisits(entry)}
                          </span>
                        )}
                      </td>
                      <td className='p-3'>
                        {showBreakdown ? (
                          <div className='flex items-center gap-2'>
                            <span className='font-medium'>
                              {getTotalEmrFilled(entry)}
                            </span>
                            <div className='text-xs text-muted-foreground'>
                              <div>{entry.oasis.emrFilled} OASIS</div>
                              <div>{entry.regular.emrFilled} Regular</div>
                            </div>
                          </div>
                        ) : (
                          <span className='font-medium'>
                            {getTotalEmrFilled(entry)}
                          </span>
                        )}
                      </td>
                      <td className='p-3'>
                        {showBreakdown ? (
                          <div className='flex items-center gap-2'>
                            <span className='font-medium'>
                              {getTotalExternalCompletion(entry)}
                            </span>
                            <div className='text-xs text-muted-foreground'>
                              <div>{entry.oasis.externalCompletion} OASIS</div>
                              <div>
                                {entry.regular.externalCompletion} Regular
                              </div>
                            </div>
                          </div>
                        ) : (
                          <span className='font-medium'>
                            {getTotalExternalCompletion(entry)}
                          </span>
                        )}
                      </td>
                      <td className='p-3'>
                        {showBreakdown ? (
                          <div className='flex items-center gap-2'>
                            <span className='font-medium'>
                              {totalPercentage}
                            </span>
                            <div className='text-xs text-muted-foreground'>
                              <div>
                                {getVisitRogerVsExternalPercentage(entry.oasis)}
                                % OASIS
                              </div>
                              <div>
                                {getVisitRogerVsExternalPercentage(
                                  entry.regular
                                )}
                                % Regular
                              </div>
                            </div>
                          </div>
                        ) : (
                          <span className='font-medium'>{totalPercentage}</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

export default VisitCompletionByClinicianReportTable;
